import { ArrowRight, Download, Package as PackageIcon } from "lucide-react";
import { toast } from "sonner";
import { products } from "@/data/site";
import { IDS } from "@/constants/testIds";
import { useQuote } from "@/components/site/QuoteDialog";
import Reveal from "@/components/site/Reveal";

const notifyDownloadComingSoon = (label) =>
  toast.info(`${label} will be available shortly — please request via WhatsApp or the enquiry form.`);

export default function Products() {
  const { open } = useQuote();

  return (
    <section
      id="products"
      data-testid={IDS.productsSection}
      className="relative py-24 sm:py-32 bg-cream"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
          <div className="max-w-2xl">
            <span className="text-xs uppercase tracking-[0.25em] font-semibold text-brand">
              Product Categories
            </span>
            <h2 className="mt-4 font-heading text-4xl sm:text-5xl font-bold leading-[1.08] tracking-tight text-ink">
              Sustainable choices for a{" "}
              <span className="font-serif-accent italic font-medium text-brand">
                better tomorrow.
              </span>
            </h2>
          </div>
          <p className="text-mist max-w-md text-base">
            Every category is fully customisable — sizes, GSM, printing and
            packing tailored to your market and brand.
          </p>
        </Reveal>

        <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
          {products.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.1} className="h-full">
              <article
                data-testid={IDS.productCard(p.id)}
                className="group relative h-full bg-white rounded-3xl overflow-hidden border border-line hover:border-forest/25 shadow-sm hover:shadow-2xl hover:shadow-forest/10 transition-all duration-500 hover:-translate-y-1.5"
              >
                {/* Spotlight image frame */}
                <div className="relative aspect-[4/3] overflow-hidden bg-cream">
                  <img
                    src={p.image}
                    alt={p.name}
                    className={`h-full w-full ${p.fit === "contain" ? "object-contain" : "object-cover"} object-center group-hover:scale-105 transition-transform duration-700 ease-out`}
                    loading="lazy"
                  />
                  {/* spotlight glow */}
                  <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.35),transparent_55%)] opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-forest/70 via-forest/10 to-transparent" />
                  <div className="absolute top-4 right-4">
                    <span className="text-[10px] uppercase tracking-[0.2em] font-semibold bg-sage text-forest px-2.5 py-1 rounded-full shadow-sm">
                      0{i + 1}
                    </span>
                  </div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="font-heading text-2xl font-semibold text-white drop-shadow-md">
                      {p.name}
                    </h3>
                    <p className="text-white/85 text-sm drop-shadow">{p.tagline}</p>
                  </div>
                </div>

                {/* Body */}
                <div className="p-6 sm:p-7">
                  <div className="flex flex-wrap gap-1.5 mb-5">
                    {p.types.map((t) => (
                      <span
                        key={t}
                        className="text-xs bg-sage text-forest px-2.5 py-1 rounded-full font-medium"
                      >
                        {t}
                      </span>
                    ))}
                  </div>

                  <dl className="divide-y divide-line border-y border-line">
                    {p.specs.map((s) => (
                      <div
                        key={s.label}
                        className="flex items-start justify-between gap-4 py-2.5"
                      >
                        <dt className="text-xs uppercase tracking-wider text-mist font-semibold min-w-[80px]">
                          {s.label}
                        </dt>
                        <dd className="text-sm text-ink text-right flex-1">
                          {s.value}
                        </dd>
                      </div>
                    ))}
                  </dl>

                  {p.variants?.length ? (
                    <div className="mt-6">
                      <div className="text-xs uppercase tracking-[0.18em] text-mist font-semibold mb-3">
                        Available Types
                      </div>
                      <div className={`grid gap-2 ${p.variants.length === 2 ? "grid-cols-2" : "grid-cols-3"}`}>
                        {p.variants.map((v) => (
                          <div
                            key={v.name}
                            className="group/variant relative rounded-xl overflow-hidden bg-white border border-line aspect-square"
                          >
                            <img
                              src={v.image}
                              alt={v.name}
                              loading="lazy"
                              className="h-full w-full object-contain p-2 group-hover/variant:scale-105 transition-transform duration-500"
                            />
                            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-forest/90 via-forest/50 to-transparent p-2 pt-6">
                              <div className="text-[10px] leading-tight font-semibold text-white text-center line-clamp-2">
                                {v.name}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  <div className="mt-6 flex flex-col sm:flex-row gap-2">
                    <button
                      onClick={() => open(p.name)}
                      data-testid={IDS.productRequestQuote(p.id)}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-brand text-white hover:bg-forest px-4 py-2.5 text-sm font-semibold transition-colors duration-300"
                    >
                      Request Quote <ArrowRight className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => notifyDownloadComingSoon(`${p.name} spec sheet`)}
                      data-testid={IDS.productDownloadSpecs(p.id)}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full border border-brand text-brand hover:bg-sage px-4 py-2.5 text-sm font-semibold transition-colors duration-300"
                    >
                      <Download className="h-4 w-4" /> Download
                    </button>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        {/* Future categories */}
        <Reveal className="mt-16">
          <div className="relative overflow-hidden bg-forest rounded-3xl p-8 sm:p-12 flex flex-col md:flex-row md:items-center gap-6 shadow-xl shadow-forest/20">
            <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-brand/30 blur-3xl pointer-events-none" />
            <div className="relative inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-sage/15 text-sage shrink-0">
              <PackageIcon className="h-8 w-8" strokeWidth={1.6} />
            </div>
            <div className="relative flex-1">
              <div className="text-[11px] uppercase tracking-[0.25em] font-semibold text-sage">
                Coming Soon
              </div>
              <div className="mt-2 font-heading text-2xl sm:text-3xl font-bold text-white tracking-tight">
                More categories on the way
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {["Food packaging", "Wooden cutlery", "Bagasse", "Areca leaf", "Palm leaf", "Sugarcane fibre", "Bamboo products"].map((c) => (
                  <span
                    key={c}
                    className="text-sm sm:text-base font-medium bg-white/10 text-sage border border-white/15 px-4 py-1.5 rounded-full"
                  >
                    {c}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
